import { ShieldWarning, ShieldCheck, Warning, HandHeart, ListChecks, SpeakerHigh, Stop } from '@phosphor-icons/react';
import { useState } from 'react';
import { toast } from 'sonner';
import RiskGauge from './RiskGauge';
import ShareCard from './ShareCard';
import ReasoningPanel from './ReasoningPanel';
import { speak, stopSpeaking } from '@/lib/voice';
import { getRole } from '@/components/RoleGate';
import { ANALYZE } from '@/constants/testIds/analysis';

const bandMeta = (score) => {
  if (score >= 75) return { color: 'text-red-400', border: 'border-red-500/50', bg: 'bg-red-500/10', icon: ShieldWarning };
  if (score >= 50) return { color: 'text-amber-400', border: 'border-amber-500/50', bg: 'bg-amber-500/10', icon: Warning };
  if (score >= 25) return { color: 'text-yellow-400', border: 'border-yellow-500/50', bg: 'bg-yellow-500/10', icon: Warning };
  return { color: 'text-emerald-400', border: 'border-emerald-500/50', bg: 'bg-emerald-500/10', icon: ShieldCheck };
};

const sevPill = (s) => ({
  low: 'border-yellow-500/40 text-yellow-400 bg-yellow-500/5',
  medium: 'border-amber-500/50 text-amber-400 bg-amber-500/10',
  high: 'border-red-500/50 text-red-400 bg-red-500/10',
}[s] || 'border-zinc-700 text-zinc-300 bg-zinc-800/40');

export default function AnalysisReport({ data, testId }) {
  const [speaking, setSpeaking] = useState(false);
  if (!data) return null;
  const kid = getRole() === 'child';
  const meta = bandMeta(data.risk_score ?? 0);
  const Icon = meta.icon;

  const listen = async () => {
    if (speaking) { stopSpeaking(); setSpeaking(false); return; }
    const script = `${data.risk_label}. Risk ${Math.round(data.risk_score ?? 0)} out of 100. ${data.one_line_summary || data.reason || ''}`;
    setSpeaking(true);
    try {
      const a = await speak(script);
      a.onended = () => setSpeaking(false);
    } catch { toast.error('Could not play audio'); setSpeaking(false); }
  };

  return (
    <div className="panel p-6 md:p-8" data-testid={testId || ANALYZE.reportPanel}>
      <div className="grid gap-8 md:grid-cols-[240px,1fr]">
        <div>
          <RiskGauge score={data.risk_score ?? 0} label={data.risk_label ?? 'Safe'} testId={ANALYZE.riskScore} />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className={`inline-flex items-center gap-2 border ${meta.border} ${meta.bg} px-2.5 py-1`} data-testid={ANALYZE.riskLabel}>
              <Icon weight="fill" className={`h-3.5 w-3.5 ${meta.color}`} />
              <span className={`text-[10px] uppercase tracking-[0.28em] font-bold ${meta.color}`}>{data.risk_label}</span>
            </div>
            <button onClick={listen} data-testid="analysis-listen-btn"
              className="inline-flex items-center gap-1.5 border border-zinc-800 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:border-emerald-500 hover:text-emerald-400">
              {speaking ? <Stop weight="fill" className="h-3.5 w-3.5" /> : <SpeakerHigh weight="bold" className="h-3.5 w-3.5" />}
              {speaking ? 'stop' : 'listen'}
            </button>
          </div>
          <h3 className="mt-4 font-sans text-2xl font-black tracking-tight text-zinc-100 leading-tight" data-testid={ANALYZE.summaryText}>
            {data.one_line_summary || data.reason || 'No summary'}
          </h3>
          {typeof data.confidence === 'number' && (
            <p className="mt-2 text-xs text-zinc-500">model confidence · {Math.round(data.confidence * 100)}%</p>
          )}
        </div>
      </div>

      {!kid && Array.isArray(data.detected_tactics) && data.detected_tactics.length > 0 && (
        <Section title="Detected Tactics" icon={Warning}>
          <div className="flex flex-wrap gap-2 mb-4" data-testid={ANALYZE.tacticsList}>
            {data.detected_tactics.map((t, i) => (
              <span key={i} className={`inline-flex items-center gap-2 border px-2.5 py-1 text-[11px] uppercase tracking-[0.16em] ${sevPill(t.severity)}`}>
                <span className="font-bold">{t.tactic}</span>
                <span className="opacity-60">· {t.severity}</span>
              </span>
            ))}
          </div>
          <div className="space-y-2">
            {data.detected_tactics.filter(t => t.evidence).map((t, i) => (
              <div key={i} className={`border-l-2 ${sevPill(t.severity).split(' ')[0]} pl-3 py-1 text-xs text-zinc-300 leading-relaxed`}>
                <span className="text-zinc-500 mr-2">↳</span>"{t.evidence}"
              </div>
            ))}
          </div>
        </Section>
      )}

      {Array.isArray(data.body_language) && data.body_language.length > 0 && (
        <Section title="Body Language Read" icon={Warning}>
          <ul className="space-y-2 text-sm text-zinc-300">
            {data.body_language.map((b, i) => (
              <li key={i} className="grid grid-cols-[140px,1fr] gap-4 border-b border-zinc-800 py-2 last:border-0">
                <span className="label-mini pt-0.5">{b.cue}</span>
                <span>{b.reading}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {Array.isArray(data.behavioral_risks) && data.behavioral_risks.length > 0 && (
        <Section title="Behavioral Risks" icon={ShieldWarning}>
          <div className="space-y-2">
            {data.behavioral_risks.map((b, i) => (
              <div key={i} className={`border ${sevPill(b.confidence)} p-3`}>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-sm">{b.risk}</span>
                  <span className="label-mini">conf · {b.confidence}</span>
                </div>
                {b.evidence && <p className="mt-2 text-xs text-zinc-400">"{b.evidence}"</p>}
              </div>
            ))}
          </div>
          {data.disclaimer && <p className="mt-3 text-[11px] italic text-zinc-500">{data.disclaimer}</p>}
        </Section>
      )}

      <div className="mt-8 grid gap-6 md:grid-cols-2">
        {Array.isArray(data.potential_harms) && data.potential_harms.length > 0 && (
          <Column title="Potential Harms" tone="red" testId={ANALYZE.harmsList} items={data.potential_harms} />
        )}
        {Array.isArray(data.potential_benefits) && data.potential_benefits.length > 0 && (
          <Column title="Potential Benefits" tone="emerald" testId={ANALYZE.benefitsList} items={data.potential_benefits} icon={HandHeart} />
        )}
        {Array.isArray(data.red_flags) && data.red_flags.length > 0 && (
          <Column title="Red Flags" tone="red" items={data.red_flags.map(f => `${f.flag} — ${f.evidence || ''}`)} />
        )}
        {Array.isArray(data.green_flags) && data.green_flags.length > 0 && (
          <Column title="Green Flags" tone="emerald" items={data.green_flags.map(f => `${f.flag} — ${f.evidence || ''}`)} icon={HandHeart} />
        )}
        {Array.isArray(data.wellbeing_signals) && data.wellbeing_signals.length > 0 && (
          <Column title="Wellbeing Signals" tone="emerald" items={data.wellbeing_signals} icon={HandHeart} />
        )}
      </div>

      {Array.isArray(data.recommended_actions) && data.recommended_actions.length > 0 && (
        <Section title="Recommended Actions" icon={ListChecks}>
          <ol className="space-y-2 text-sm" data-testid={ANALYZE.actionsList}>
            {data.recommended_actions.map((a, i) => (
              <li key={i} className="flex gap-3 border-l-2 border-zinc-100 pl-3 py-1">
                <span className="label-mini w-6 pt-0.5">{String(i + 1).padStart(2, '0')}</span>
                <span className="text-zinc-200">{a}</span>
              </li>
            ))}
          </ol>
        </Section>
      )}
      {kid && (data.risk_score ?? 0) >= 45 && (
        <div className="mt-6 border border-amber-500/40 bg-amber-500/5 p-4" data-testid="kid-safety-note">
          <h4 className="label-mini text-amber-400">what to do</h4>
          <p className="mt-2 text-sm text-zinc-200 leading-relaxed">
            This message doesn't feel right. You did the smart thing by checking. Don't reply yet — show it to a parent,
            guardian, or another adult you trust. You're not in trouble, and you're not alone.
          </p>
        </div>
      )}
      {!kid && <ReasoningPanel reasoning={data.reasoning} />}
      <ShareCard data={data} />
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="mt-8 border-t border-zinc-800 pt-6">
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon weight="bold" className="h-4 w-4 text-zinc-500" />}
        <h4 className="label-mini">{title}</h4>
      </div>
      {children}
    </div>
  );
}

function Column({ title, tone, items, icon: Icon, testId }) {
  const toneCls = tone === 'red'
    ? 'border-red-500/30 text-red-400'
    : 'border-emerald-500/30 text-emerald-400';
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon weight="bold" className={`h-4 w-4 ${toneCls.split(' ')[1]}`} />}
        <h4 className="label-mini">{title}</h4>
      </div>
      <ul className={`space-y-1 border-l-2 ${toneCls.split(' ')[0]} pl-3 text-sm text-zinc-300`} data-testid={testId}>
        {items.map((it, i) => <li key={i} className="leading-relaxed">— {it}</li>)}
      </ul>
    </div>
  );
}
